import { useCallback, useMemo, useState } from 'react';
import type {
  ColumnOrderState,
  ColumnPinningState,
  ColumnSizingState,
  OnChangeFn,
  PaginationState,
  SortingState,
  VisibilityState,
  Updater,
} from '@tanstack/react-table';
import { usePreference } from './usePreference';
import {
  usePreferencesStore,
} from '@/state/preferences';
import { deletePreference } from '@/lib/preferencesApi';
import type { GridPreferences } from '@/types/preferences';

export interface GridDefaults {
  columnVisibility?: VisibilityState;
  columnOrder?: ColumnOrderState;
  columnSizing?: ColumnSizingState;
  columnPinning?: ColumnPinningState;
  pageSize?: number;
  sorting?: SortingState;
}

function resolveUpdater<T>(updater: Updater<T>, prev: T): T {
  return typeof updater === 'function' ? (updater as (p: T) => T)(prev) : updater;
}

export function useGridPreferences(gridKey: string, defaults: GridDefaults) {
  const path = `grids.${gridKey}`;

  const initial = useMemo<GridPreferences>(
    () => ({
      columnVisibility: defaults.columnVisibility ?? {},
      columnOrder: defaults.columnOrder ?? [],
      columnSizing: defaults.columnSizing ?? {},
      columnPinning: defaults.columnPinning ?? { left: [], right: [] },
      pageSize: defaults.pageSize ?? 25,
      sorting: defaults.sorting ?? [],
    }),
    // defaults are expected to be stable references from the consumer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [stored, setStored] = usePreference<GridPreferences>(path, initial);

  // pageIndex is tracked in local React state only — never persisted to the
  // server (we don't want users' current page synced across devices). But we
  // DO need to track it in controlled mode or TanStack Table snaps back to 0
  // every render.
  const [pageIndex, setPageIndex] = useState<number>(0);

  const pageSize = stored.pageSize ?? initial.pageSize ?? 25;

  const state = useMemo(
    () => ({
      columnVisibility: stored.columnVisibility ?? initial.columnVisibility ?? {},
      columnOrder: stored.columnOrder ?? initial.columnOrder ?? [],
      columnSizing: stored.columnSizing ?? initial.columnSizing ?? {},
      columnPinning:
        stored.columnPinning ?? initial.columnPinning ?? { left: [], right: [] },
      sorting: stored.sorting ?? initial.sorting ?? [],
      pagination: {
        pageIndex,
        pageSize,
      } as PaginationState,
    }),
    [stored, initial, pageIndex, pageSize]
  );

  const onColumnVisibilityChange: OnChangeFn<VisibilityState> = useCallback(
    (updater) => {
      const prev = state.columnVisibility;
      setStored({ ...stored, columnVisibility: resolveUpdater(updater, prev) });
    },
    [setStored, state.columnVisibility, stored]
  );

  const onColumnOrderChange: OnChangeFn<ColumnOrderState> = useCallback(
    (updater) => {
      const prev = state.columnOrder;
      setStored({ ...stored, columnOrder: resolveUpdater(updater, prev) });
    },
    [setStored, state.columnOrder, stored]
  );

  const onColumnSizingChange: OnChangeFn<ColumnSizingState> = useCallback(
    (updater) => {
      const prev = state.columnSizing;
      setStored({ ...stored, columnSizing: resolveUpdater(updater, prev) });
    },
    [setStored, state.columnSizing, stored]
  );

  const onColumnPinningChange: OnChangeFn<ColumnPinningState> = useCallback(
    (updater) => {
      const prev = state.columnPinning;
      setStored({ ...stored, columnPinning: resolveUpdater(updater, prev) });
    },
    [setStored, state.columnPinning, stored]
  );

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const prev = state.sorting;
      setStored({ ...stored, sorting: resolveUpdater(updater, prev) });
      // Sort changed -> back to page 1 (matches TanStack uncontrolled default).
      setPageIndex(0);
    },
    [setStored, state.sorting, stored]
  );

  const onPaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updater) => {
      const next = resolveUpdater(updater, state.pagination);
      const pageSizeChanged = next.pageSize !== state.pagination.pageSize;

      if (pageSizeChanged) {
        // Only persist when pageSize actually changes — this avoids a no-op
        // PATCH on every page click (pageIndex is not persisted).
        setStored({ ...stored, pageSize: next.pageSize });
        // New pageSize means "page 1 of the new size" — reset pageIndex.
        setPageIndex(0);
      } else {
        setPageIndex(next.pageIndex);
      }
    },
    [setStored, state.pagination, stored]
  );

  const resetPage = useCallback(() => {
    setPageIndex(0);
  }, []);

  const reset = useCallback(() => {
    // Remove locally and fire DELETE. Silent on failure — local removal stands
    // and the next /me refetch will reconcile.
    usePreferencesStore.getState().forgetPath(path);
    deletePreference(path).catch(() => {});
  }, [path]);

  return {
    state,
    onColumnVisibilityChange,
    onColumnOrderChange,
    onColumnSizingChange,
    onColumnPinningChange,
    onSortingChange,
    onPaginationChange,
    resetPage,
    reset,
  };
}
